import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function Organizations({ isOwner }) {
  const [orgs, setOrgs] = useState([]);
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/orgs").then((r) => setOrgs(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !adminEmail.trim()) return toast.error("Company name and admin email are required");
    setSaving(true);
    try {
      await api.post("/orgs", { name, admin_email: adminEmail });
      toast.success(`${name} added — invite emailed to ${adminEmail}`); setName(""); setAdminEmail(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const rename = async (o) => {
    const n = window.prompt("Organization name", o.name);
    if (!n || n === o.name) return;
    await api.patch(`/orgs/${o.id}`, { name: n }); toast.success("Renamed"); load();
  };
  const addAdmin = async (o) => {
    const email = window.prompt(`Email of an additional admin for ${o.name}. They will be pre-approved as Admin and emailed a sign-in link.`);
    if (!email) return;
    try { await api.post(`/orgs/${o.id}/admins`, { email }); toast.success(`Invite sent to ${email}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (o) => {
    const typed = window.prompt(`This permanently deletes ${o.name}, all of its jobs, cases, companies, servers, affidavits, forms and its ${o.user_count ?? 0} user account(s). Type the company name to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== o.name) return toast.error("Name did not match — nothing deleted");
    try { await api.delete(`/orgs/${o.id}`); toast.success(`${o.name} deleted`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="organizations-card">
      <div className="sm-label">Organizations</div>
      <h2 className="text-lg font-semibold">{isOwner ? "Companies sharing this site" : "Your organization"}</h2>
      <p className="text-sm text-slate-500">Each organization has completely separate jobs, cases, companies, servers, forms and branding. Users only ever see their own organization's data.{isOwner && " Use the checkboxes to let a partner company create asset-search and record-retrieval jobs in addition to process service."}</p>
      <div className="divide-y divide-slate-100 border-t border-slate-200 mt-4">
        {orgs.map((o) => (
          <div key={o.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`org-row-${o.id}`}>
            <Building2 className="h-4 w-4 text-[#2d7bd0] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{o.name}</div>
              {o.admin_email && <div className="text-xs text-slate-500 truncate" data-testid={`org-admin-${o.id}`}>Admin: {o.admin_email}</div>}
            </div>
            {isOwner && <div className="text-xs text-slate-500 whitespace-nowrap">{o.user_count ?? 0} users · {o.job_count ?? 0} jobs</div>}
            {isOwner && <div className="flex items-center gap-3 text-xs" data-testid={`org-features-${o.id}`}>
              {[["asset_search", "Asset searches"], ["record_retrieval", "Record retrieval"], ["document_courier", "Document drop-off / pick-up"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-1 cursor-pointer whitespace-nowrap" title={`Allow ${o.name} to create ${l.toLowerCase()} jobs`}>
                  <input type="checkbox" className="accent-[#2d7bd0]" checked={!!o.features?.[k]} data-testid={`org-feature-${k}-${o.id}`} onChange={async (e) => {
                    try { await api.patch(`/orgs/${o.id}/features`, { [k]: e.target.checked }); toast.success(`${l} ${e.target.checked ? "enabled" : "disabled"} for ${o.name}`); load(); }
                    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                  }} />{l}
                </label>
              ))}
            </div>}
            <button type="button" className="sm-link text-xs whitespace-nowrap" onClick={() => addAdmin(o)} data-testid={`add-admin-${o.id}`}><UserPlus className="inline h-3 w-3 mr-0.5" />Add admin</button>
            <button type="button" className="sm-link text-xs" onClick={() => rename(o)} data-testid={`rename-org-${o.id}`}>Rename</button>
            {isOwner && o.id !== "org_default" && (
              <button type="button" className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-0.5" onClick={() => remove(o)} data-testid={`delete-org-${o.id}`}><Trash2 className="h-3 w-3" />Delete</button>
            )}
          </div>
        ))}
      </div>
      {isOwner && (
        <form onSubmit={add} className="mt-5 rounded border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="new-org-form">
          <div className="text-sm font-semibold flex items-center gap-2"><Plus className="h-4 w-4" />Add a company</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="sm-label">Company name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Process Service" data-testid="new-org-name" /></div>
            <div className="space-y-1"><Label className="sm-label">Their admin's Google email</Label><Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="owner@acmeserve.com" data-testid="new-org-admin-email" /></div>
          </div>
          <p className="text-xs text-slate-500">That person is pre-approved as the company's Admin: they receive an email now, and the first time they sign in with Google they land directly in their own workspace. From there they approve their own staff.</p>
          <Button type="submit" className="sm-green" disabled={saving} data-testid="new-org-submit">{saving ? "Adding…" : "Add company & send invite"}</Button>
        </form>
      )}
    </div>
  );
}
